import { useContext, useState, useCallback, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FaPlus, FaFilter } from 'react-icons/fa';
import { FiFileText } from 'react-icons/fi';
import { MdDelete, MdMoreHoriz } from 'react-icons/md';
import { Link } from 'react-router-dom';
import AuthenticateContext from '../../provider/context/authenticate.context';
import { showSuccessMessage } from '../../module/message';
import {
  getBottlerList,
  batchDeleteBottlerGroup,
  deleteBottlerGroup,
} from '../../module/bottler';
import handleApiResponse from '../../utils/api/handleApiResponse';
import {
  Section,
  SectionBody,
  SectionHeader,
  SectionToolbar,
  SectionToolbarItem,
} from '../../components/section';
import { Heading1 } from '../../components/heading';
import { Button, IconButton, ButtonGroup } from '../../components/button';
import Dropdown, {
  DropdownItem,
  DropdownButtonOption,
  DropdownLinkOption,
} from '../../components/dropdown';
import { Table } from '../../components/table';
import { useLimitChange, usePageChange } from '../../components/table/hooks';
import { StateModal, TitleModal } from '../../components/modal';
import { Grid, Column } from '../../components/grid';
import { FormItem, Input, Label, Checkbox } from '../../components/form';

const BottlerGroupList = () => {
  const { t: trans } = useTranslation();
  const { userInformation } = useContext(AuthenticateContext);
  const [selectedBottlerGroup, setSelectedBottlerGroup] = useState([]);
  const [isSelectedAll, setIsSelectedAll] = useState(false);
  const [listData, setListData] = useState([]);
  const [listParams, setListParams] = useState({
    page: 1,
    limit: 10,
    filters: {
      name: '',
      remark: '',
    },
  });
  const [total, setTotal] = useState(1);

  const onLimitChange = useLimitChange(setListParams);
  const onPageChange = usePageChange(setListParams);

  const handleSelectedChange = (id) => {
    setSelectedBottlerGroup((previous) => {
      const newSelectedBottlerGroup = [...previous];
      const index = previous.indexOf(id);
      if (index === -1) {
        newSelectedBottlerGroup.push(id);
      } else {
        newSelectedBottlerGroup.splice(index, 1);
      }

      return newSelectedBottlerGroup;
    });
  };

  const handleSelectAll = useCallback(() => {
    if (isSelectedAll) {
      setSelectedBottlerGroup([]);
    } else {
      const newSelectedBottlerGroup = [];
      listData.forEach((bottlerGroup) => {
        newSelectedBottlerGroup.push(bottlerGroup.id);
      });
      setSelectedBottlerGroup(newSelectedBottlerGroup);
    }

    setIsSelectedAll(!isSelectedAll);
  }, [isSelectedAll, listData]);

  const handleGetBottlerList = useCallback(() => {
    const requestData = {
      page: listParams.page,
      limit: listParams.limit,
    };
    if (listParams.filters.name.trim()) {
      requestData.name = listParams.filters.name.trim();
    }
    if (listParams.filters.remark.trim()) {
      requestData.remark = listParams.filters.remark.trim();
    }

    handleApiResponse(getBottlerList(requestData), (response) => {
      const { total, list } = response.data.data;
      setTotal(total);
      setListData(list);
      setSelectedBottlerGroup([]);
    });
  }, [listParams]);

  const handleBatchDeleteBottlerGroup = (ids) => {
    handleApiResponse(batchDeleteBottlerGroup(ids), () => {
      showSuccessMessage({ message: trans('success:RemoveBottlerGroup') });
      handleGetBottlerList();
    });
  };

  const handleDeleteBottlerGroup = useCallback(
    (id) => {
      handleApiResponse(deleteBottlerGroup(id), () => {
        showSuccessMessage({ message: trans('success:RemoveBottlerGroup') });
        handleGetBottlerList();
      });
    },
    [trans, handleGetBottlerList],
  );

  const handleBatchRemove = (ids) => {
    return StateModal({
      type: 'warning',
      title: trans('AreYouSure'),
      text: trans('DoYouWantToRemoveTheseBottlerGroups'),
      showCancelButton: true,
      confirmButtonText: trans('button:Confirm'),
      cancelButtonText: trans('button:Cancel'),
      allowDismiss: true,
      onConfirm() {
        handleBatchDeleteBottlerGroup(ids);
      },
    });
  };

  const handleRemove = useCallback(
    (id) => {
      return StateModal({
        type: 'warning',
        title: trans('AreYouSure'),
        text: trans('DoYouWantToRemoveThisBottlerGroup'),
        showCancelButton: true,
        confirmButtonText: trans('button:Confirm'),
        cancelButtonText: trans('button:Cancel'),
        allowDismiss: true,
        onConfirm() {
          handleDeleteBottlerGroup(id);
        },
      });
    },
    [trans, handleDeleteBottlerGroup],
  );

  const generateActionDropdown = useCallback(
    ({ id }) => {
      return (
        <Dropdown>
          <Dropdown.Toggle>
            <IconButton>
              <MdMoreHoriz />
            </IconButton>
          </Dropdown.Toggle>
          <Dropdown.Content>
            <DropdownItem>
              <DropdownLinkOption to={`/bottler-group/${id}`}>
                <FiFileText />
                {trans('button:Details')}
              </DropdownLinkOption>
            </DropdownItem>
            {userInformation.permissions.includes('bottler.delete') && (
              <DropdownItem onClick={() => handleRemove(id)}>
                <DropdownButtonOption>
                  <MdDelete />
                  {trans('button:Remove')}
                </DropdownButtonOption>
              </DropdownItem>
            )}
          </Dropdown.Content>
        </Dropdown>
      );
    },
    [trans, userInformation.permissions, handleRemove],
  );

  const tableColumns = useMemo(() => {
    const columns = [
      {
        title: trans('Name'),
        fieldName: 'name',
      },
      {
        title: trans('SalesCenterCount'),
        fieldName: 'saleCenterCount',
      },
      {
        title: trans('TerminalCount'),
        fieldName: 'terminalCount',
      },
      {
        title: trans('Description'),
        fieldName: 'remark',
      },
      {
        title: trans('Action'),
        custom: true,
        width: '10%',
        align: 'center',
        render(data) {
          return generateActionDropdown(data);
        },
      },
    ];

    if (userInformation.permissions.includes('bottler.delete')) {
      columns.unshift({
        title: (
          <Checkbox
            noMargin
            onChange={handleSelectAll}
            checked={isSelectedAll}
          />
        ),
        fieldName: 'id',
        sortable: false,
        width: '60px',
        render(data) {
          return (
            <Checkbox
              noMargin
              onChange={() => {
                handleSelectedChange(data);
              }}
              checked={selectedBottlerGroup.includes(data)}
            />
          );
        },
      });
    }

    return columns;
  }, [
    trans,
    generateActionDropdown,
    userInformation.permissions,
    handleSelectAll,
    isSelectedAll,
    selectedBottlerGroup,
  ]);

  const showFilterPanel = () => {
    TitleModal({
      size: 'normal',
      children: (
        <BottlerGroupListFilter
          listParameters={listParams}
          setListParameters={setListParams}
        />
      ),
    });
  };

  useEffect(() => {
    handleGetBottlerList();
  }, [handleGetBottlerList]);

  useEffect(() => {
    if (
      listData.length !== 0 &&
      selectedBottlerGroup.length === listData.length
    ) {
      setIsSelectedAll(true);
    } else {
      setIsSelectedAll(false);
    }
  }, [listData, selectedBottlerGroup]);

  return (
    <>
      <Section noPadding>
        <SectionHeader sticky backgroundFill>
          <SectionToolbar>
            <SectionToolbarItem>
              <Heading1>{trans('BottlerGroupList')}</Heading1>
            </SectionToolbarItem>
            <SectionToolbarItem>
              {userInformation.permissions.includes('bottler.delete') &&
                selectedBottlerGroup.length > 0 && (
                  <Button
                    secondary
                    onClick={() => handleBatchRemove(selectedBottlerGroup)}
                  >
                    <MdDelete />
                    {trans('Remove')}
                  </Button>
                )}
              {userInformation.permissions.includes('bottler.create') && (
                <Link to='/bottler-group/create'>
                  <Button>
                    <FaPlus />
                    {trans('button:Create')}
                  </Button>
                </Link>
              )}
              <Button secondary onClick={showFilterPanel}>
                <FaFilter />
                {trans('Filter')}
              </Button>
            </SectionToolbarItem>
          </SectionToolbar>
        </SectionHeader>
        <SectionBody>
          <Table
            columns={tableColumns}
            data={listData}
            currentPage={listParams.page}
            limit={listParams.limit}
            total={total}
            onLimitChange={onLimitChange}
            onPageChange={onPageChange}
            translation={{
              info: trans('table.info'),
              empty: trans('table.empty'),
            }}
            select={{ type: 'multiple', matcher: 'id' }}
            selected={selectedBottlerGroup}
          />
        </SectionBody>
      </Section>
    </>
  );
};

const BottlerGroupListFilter = ({
  listParameters,
  setListParameters,
  close,
}) => {
  const { t: trans } = useTranslation();
  const [searchData, setSearchData] = useState({
    ...listParameters.filters,
  });

  const handleChangeSearchKeyword = (key, value) => {
    setSearchData({ ...searchData, [key]: value });
  };

  const handleSearch = () => {
    setListParameters((previousParams) => {
      return {
        ...previousParams,
        page: 1,
        filters: searchData,
      };
    });
    close();
  };

  const handleReset = () => {
    setSearchData({
      name: '',
      remark: '',
    });
  };

  return (
    <>
      <TitleModal.Header handleClose={close}>
        {trans('Filter')}
      </TitleModal.Header>
      <TitleModal.Body noPaddingBottom>
        <Grid columns={2}>
          <Column desktop={1}>
            <FormItem>
              <Label htmlFor='name'>{trans('Name')}</Label>
              <Input
                type='text'
                id='name'
                value={searchData.name}
                onChange={(event) => {
                  handleChangeSearchKeyword('name', event.target.value);
                }}
              />
            </FormItem>
          </Column>
          <Column desktop={1}>
            <FormItem>
              <Label htmlFor='description'>{trans('Description')}</Label>
              <Input
                type='text'
                id='description'
                value={searchData.remark}
                onChange={(event) => {
                  handleChangeSearchKeyword('remark', event.target.value);
                }}
              />
            </FormItem>
          </Column>
        </Grid>
      </TitleModal.Body>
      <TitleModal.Footer>
        <ButtonGroup>
          <Button danger onClick={handleReset}>
            {trans('button:Reset')}
          </Button>
          <Button danger onClick={close}>
            {trans('button:Cancel')}
          </Button>
          <Button success onClick={handleSearch}>
            {trans('button:Search')}
          </Button>
        </ButtonGroup>
      </TitleModal.Footer>
    </>
  );
};

export default BottlerGroupList;
